import React, { useEffect, useState } from 'react'
import {
  ArrowLeft,
  Trash2,
  Clock,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  MessageSquare,
  Search,
} from 'lucide-react'
import {
  collectionGroup,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { useNavigate } from 'react-router-dom'

export default function CleanOldMessages() {
  const [status, setStatus] = useState('idle')
  const [log, setLog] = useState([])
  const [deletedCount, setDeletedCount] = useState(0)
  const [days, setDays] = useState(2)
  const [messages, setMessages] = useState([])
  const [loadingMsg, setLoadingMsg] = useState(true)
  const [selected, setSelected] = useState([])
  const [search, setSearch] = useState('')

  const navigate = useNavigate()

  // ============================================================
  // 🔹 LOAD PESAN TERBARU — hanya session yang sudah berakhir
  // ============================================================
  useEffect(() => {
    const q = query(
      collectionGroup(db, 'messages'),
      where('sessionActive', '==', false),
      orderBy('timestamp', 'desc'),
      limit(50)
    )

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data(),
        }))
        setMessages(list)
        setLoadingMsg(false)
      },
      (error) => {
        console.error('🔥 Firestore error:', error)
        setLoadingMsg(false)
        setMessages([])
        setLog(['⚠️ Gagal memuat data: ' + error.message])
      }
    )

    return () => unsubscribe()
  }, [])

  // ============================================================
  // 🔹 TOGGLE SELECTION
  // ============================================================
  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const selectAll = () => {
    if (selected.length === messages.length) setSelected([])
    else setSelected(messages.map((m) => m.id))
  }

  // ============================================================
  // 🔹 DELETE SELECTED (AMAN: hanya sessionActive == false)
  // ============================================================
  const handleDeleteSelected = async () => {
    if (selected.length === 0) {
      alert('Pilih minimal satu pesan untuk dihapus.')
      return
    }

    const confirmDelete = window.confirm(
      `Yakin ingin menghapus ${selected.length} pesan yang dipilih?`
    )
    if (!confirmDelete) return

    setStatus('running')
    setLog([])
    setDeletedCount(0)

    try {
      const toDelete = messages.filter(
        (msg) => selected.includes(msg.id) && msg.sessionActive === false
      )

      if (toDelete.length === 0) {
        setStatus('done')
        setLog(['⚠️ Tidak ada pesan dari sesi non-aktif yang bisa dihapus.'])
        return
      }

      await Promise.all(toDelete.map((msg) => deleteDoc(msg.ref)))

      setDeletedCount(toDelete.length)
      setSelected([])
      setStatus('done')
      setLog([`✅ ${toDelete.length} pesan berhasil dihapus.`])
    } catch (err) {
      console.error('Error deleting selected messages:', err)
      setStatus('error')
      setLog(['❌ Terjadi kesalahan: ' + err.message])
    }
  }

  // ============================================================
  // 🔹 DELETE OLD MESSAGES (AMAN: hanya sessionActive == false)
  // ============================================================
  const handleDeleteOldMessages = async () => {
    try {
      const confirmDelete = window.confirm(
        `Yakin ingin menghapus semua pesan dari sesi yang sudah berakhir dan lebih lama dari ${days} hari?`
      )
      if (!confirmDelete) return

      setStatus('running')
      setLog([])
      setDeletedCount(0)

      const cutoff = Timestamp.fromDate(
        new Date(Date.now() - days * 24 * 60 * 60 * 1000)
      )

      const messagesRef = collectionGroup(db, 'messages')
      const q = query(
        messagesRef,
        where('timestamp', '<', cutoff),
        where('sessionActive', '==', false)
      )

      const snapshot = await getDocs(q)
      const total = snapshot.docs.length
      if (total === 0) {
        setStatus('done')
        setLog([`Tidak ada pesan lama dari sesi non-aktif yang ditemukan.`])
        return
      }

      await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)))

      setDeletedCount(total)
      setStatus('done')
      setLog([`✅ ${total} pesan lama berhasil dihapus.`])
    } catch (err) {
      console.error('Error deleting old messages:', err)
      setStatus('error')
      setLog(['❌ Terjadi kesalahan: ' + err.message])
    }
  }

  // ============================================================
  // 🔍 FILTER PESAN BERDASARKAN SEARCH
  // ============================================================
  const filteredMessages = messages.filter((msg) => {
    const query = search.toLowerCase()
    return (
      msg.senderName?.toLowerCase().includes(query) ||
      msg.text?.toLowerCase().includes(query)
    )
  })

  // ============================================================
  // 🔹 RENDER PESAN
  // ============================================================
  const renderMessages = () => {
    if (loadingMsg) {
      return (
        <div className="flex justify-center items-center py-10 text-gray-500">
          <Loader2 size={18} className="animate-spin mr-2" />
          Memuat pesan...
        </div>
      )
    }

    if (filteredMessages.length === 0) {
      return (
        <div className="text-center py-10 text-gray-400">
          Tidak ada pesan ditemukan.
        </div>
      )
    }

    return (
      <div className="max-h-[420px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
        <div className="flex items-center justify-between p-2 bg-gray-100 border-b border-gray-200 text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected.length === messages.length}
              onChange={selectAll}
            />
            <span>Pilih Semua ({filteredMessages.length})</span>
          </div>
          {selected.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1 text-red-600 hover:text-red-700 text-xs font-medium"
            >
              <Trash2 size={14} /> Hapus yang Dipilih
            </button>
          )}
        </div>

        {filteredMessages.map((msg) => (
          <div
            key={msg.id}
            className="flex items-start gap-3 p-3 border-b last:border-0 border-gray-100 bg-white hover:bg-gray-50 transition"
          >
            <input
              type="checkbox"
              checked={selected.includes(msg.id)}
              onChange={() => toggleSelect(msg.id)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-1">
                  <MessageSquare size={14} /> {msg.senderName || 'Tanpa Nama'}
                </h3>
                <span className="text-xs text-gray-500">
                  {msg.timestamp?.toDate
                    ? new Date(msg.timestamp.toDate()).toLocaleString()
                    : '-'}
                </span>
              </div>
              {msg.text && (
                <p className="text-gray-700 text-sm mt-1">{msg.text}</p>
              )}
              {msg.imageUrl && (
                <img
                  src={msg.imageUrl}
                  alt="Chat"
                  className="mt-2 rounded-md border border-gray-200 w-40"
                />
              )}
              {msg.sessionActive && (
                <p className="text-xs text-blue-500 mt-1 font-medium">
                  💬 Pesan dari sesi aktif (tidak dapat dihapus)
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // ============================================================
  // 🔹 UI UTAMA
  // ============================================================
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition"
        >
          <ArrowLeft size={18} /> Kembali
        </button>
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <Trash2 size={20} className="text-blue-600" /> Manajemen Pesan
        </h2>
      </div>

      {/* SEARCH BAR */}
      <div className="flex items-center gap-2 mb-4">
        <Search size={16} className="text-gray-500" />
        <input
          type="text"
          placeholder="Cari pesan atau nama pengirim..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <p className="text-gray-600 mb-4 text-sm">
        Menampilkan pesan dari sesi yang sudah berakhir. Kamu dapat mencari,
        memilih, dan menghapus pesan tertentu, atau membersihkan pesan lama
        secara otomatis.
      </p>

      {/* DAFTAR PESAN */}
      <div className="mb-8">{renderMessages()}</div>

      {/* HAPUS PESAN LAMA */}
      <div className="border-t border-gray-200 pt-5 mt-5">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-gray-800">
          <Trash2 size={18} className="text-blue-600" /> Hapus Pesan Lama
        </h3>

        <div className="flex items-center gap-3 mb-4">
          <label className="text-sm font-medium text-gray-700">
            Hapus pesan lebih lama dari:
          </label>
          <input
            type="number"
            value={days}
            min={1}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-20 px-2 py-1 border rounded-md text-center"
          />
          <span className="text-gray-600 text-sm">hari</span>
        </div>

        <button
          onClick={handleDeleteOldMessages}
          disabled={status === 'running'}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium transition ${
            status === 'running'
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {status === 'running' ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Menghapus...
            </>
          ) : (
            <>
              <Trash2 size={18} />
              Hapus Pesan Lama
            </>
          )}
        </button>

        {/* STATUS & LOG */}
        <div className="mt-4">
          {status === 'done' && (
            <div className="flex items-center gap-2 text-green-600 font-medium">
              <CheckCircle2 size={18} />
              {deletedCount} pesan dihapus.
            </div>
          )}
          {status === 'error' && (
            <div className="text-red-600 font-medium">
              Terjadi kesalahan saat menghapus pesan.
            </div>
          )}
        </div>

        {log.length > 0 && (
          <div className="mt-5 p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-700 max-h-64 overflow-y-auto">
            {log.map((l, i) => (
              <div key={i}>{l}</div>
            ))}
          </div>
        )}

        {/* FOOTER */}
        <div className="mt-6 flex items-center gap-2 text-xs text-gray-500">
          <Clock size={14} />
          <span>Terakhir dijalankan: {new Date().toLocaleString()}</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-auto flex items-center gap-1 text-blue-500 hover:text-blue-700 transition"
          >
            <RefreshCcw size={14} /> Muat Ulang
          </button>
        </div>
      </div>
    </div>
  )
}
